import { useApi, useMessage } from 'app/context';
import { buildErrorMessage, ROUTES, trimFileName } from 'app/utils';
import { IReportAttachmentType, IReportRetryProcessing, IReportSchema, IReportUpdate } from 'generated/axios';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from './useForm';
import { useNavigate } from 'react-router-dom';

export const useReportDetail = (reportId: string) => {
  const { reportApi } = useApi();
  const { error, success } = useMessage();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<IReportSchema>();
  const [request, setRequest] = useState<IReportUpdate>();
  const [retryRequest, setRetryRequest] = useState<IReportRetryProcessing>();
  const [loading, setLoading] = useState<boolean>(false);
  const [photoBlisterUrl, setPhotoBlisterUrl] = useState<string>();
  const [photoLoading, setPhotoLoading] = useState<boolean>(false);

  const buildRequestFromDetail = (data: IReportSchema): IReportUpdate => ({
    doctor_id: data.doctor?.id,
    comment: data.comment,
    report_date: data.report_date,
    temperature: data.temperature,
    test_execution_date: data.test_execution_date,
    urine_quantity: data.urine_quantity,
    test_result: data.test_result,
  });

  useEffect(() => {
    if (detail) {
      buildRequestFromDetail(detail);
      setRetryRequest({ drug_test_type_override: detail.test_result?.drug_test_type });
    }
  }, [detail]);

  //GET
  const get = useCallback(() => {
    setLoading(true);
    reportApi
      .webGetReportDetails(reportId)
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        setDetail(resp.data);
        setRequest(buildRequestFromDetail(resp.data));

        //get photo blister if available
        if (resp.data.has_photo_blister) {
          reportApi
            .webDownloadReportAttachment(IReportAttachmentType.PhotoBlister, reportId, { responseType: 'blob' })
            .then((resp) => {
              if (resp.status >= 400) {
                return Promise.reject(resp);
              }
              const blob = new Blob([resp.data as unknown as BlobPart]);
              const url = window.URL.createObjectURL(blob);

              setPhotoBlisterUrl(url);
            })
            .catch((err) => {
              error({ message: buildErrorMessage(err) });
            });
        }
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportApi, reportId]);

  useEffect(() => {
    get();
  }, [get]);

  //UPDATE
  const update = (form: IReportUpdate) => {
    setLoading(true);
    reportApi
      .webUpdateReport(reportId, form)
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        success({ message: t('report.updateSuccess') as string });
        setDetail(resp.data);
        setRequest(buildRequestFromDetail(resp.data));
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
      })
      .finally(() => setLoading(false));
  };

  const updateForm = useForm<IReportUpdate>({
    defaultValues: request,
    onSubmit: update,
  });

  //GET SIGNATURE
  const getPatientSignature = () => {
    setLoading(true);
    reportApi
      .webDownloadReportAttachment(IReportAttachmentType.PatientSignature, reportId, { responseType: 'blob' })
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        const blob = new Blob([resp.data as unknown as BlobPart]);
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = trimFileName(resp.headers?.['content-disposition']) ?? 'download';
        a.click();

        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
      })
      .finally(() => setLoading(false));
  };

  //GET PHOTO BLISTER
  const getPhotoBlister = () => {
    setPhotoLoading(true);
    reportApi
      .webDownloadReportAttachment(IReportAttachmentType.PhotoBlister, reportId, { responseType: 'blob' })
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        const blob = new Blob([resp.data as unknown as BlobPart]);
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = trimFileName(resp.headers?.['content-disposition']) ?? 'download';
        a.click();

        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
      })
      .finally(() => setPhotoLoading(false));
  };

  //GET PDF
  const getPdf = () => {
    setLoading(true);
    reportApi
      .webGenerateReportPDF(reportId, { responseType: 'blob' })
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        const blob = new Blob([resp.data as unknown as BlobPart]);
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = trimFileName(resp.headers?.['content-disposition']) ?? 'download';
        a.click();

        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
      })
      .finally(() => setLoading(false));
  };

  //DELETE
  const deleteReport = useCallback(() => {
    setLoading(true);
    reportApi
      .webDeleteReport(reportId)
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        success({ message: t('report.deleteSuccess') as string });
        navigate(ROUTES.reports);
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportApi, reportId]);

  const retryReport = (form: IReportRetryProcessing) => {
    setLoading(true);
    reportApi
      .webRetryReportPhotoBlisterProcessing(reportId, form)
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        setDetail(resp.data);
        setRequest(buildRequestFromDetail(resp.data));
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
      })
      .finally(() => setLoading(false));
  };

  const retryForm = useForm<IReportRetryProcessing>({
    defaultValues: retryRequest,
    onSubmit: retryReport,
  });

  //CONFIRM
  const confirmReport = () => {
    setLoading(true);
    reportApi
      .webConfirmReport(reportId)
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        success({ message: t('report.confirmSuccess') as string });
        setDetail(resp.data);
        setRequest(buildRequestFromDetail(resp.data));
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
      })
      .finally(() => setLoading(false));
  };

  return {
    //data
    detail,
    loading,
    updateForm,
    deleteReport,
    confirmReport,
    photoBlisterUrl,

    //signature
    getPatientSignature,

    //blister
    getPhotoBlister,
    photoLoading,

    //pdf
    getPdf,

    //retry processing
    retryForm,
  };
};
